// Cookie consent state shared by the banner, the "Cookie settings" links and click-to-load embeds.
// Values: "accepted" | "rejected" | null (no choice yet = nothing optional loads).
import { useEffect, useState } from "react";

export const CONSENT_KEY = "inm_cookie_consent";
const CHANGED = "inm-consent";
const REOPEN = "inm-cookie-settings";

// Bump when the banner wording or the list of optional third parties changes; it is logged with each choice.
export const COOKIE_TEXT_VERSION = "2026-11-04";
const MAX_AGE_MS = 24 * 30.5 * 86400 * 1000; // ask again after ~24 months (AEPD cookie guide)
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* storage blocked: choice lasts for this page only */ } },
};
export const getConsent = (key = CONSENT_KEY) => {
  const v = ls.get(key);
  const at = Number(ls.get(`${key}_at`) || 0);
  if (v && at && Date.now() - at > MAX_AGE_MS) return null;
  return v;
};
const consentId = () => {
  let id = ls.get("inm_consent_id");
  if (!id || !/^[0-9a-f]{16,40}$/.test(id)) {
    const b = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(b);
    id = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    ls.set("inm_consent_id", id);
  }
  return id;
};
// Proof of consent (GDPR art. 7(1)): random browser id + choice + text version; no IP or user agent is stored.
const logConsent = (value, key) => {
  try {
    const scope = key === CONSENT_KEY ? "platform" : key.replace(/^inm_cookie_/, "");
    fetch(`${API}/public/consent`, { method: "POST", keepalive: true, headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope, choice: value, consent_id: consentId(), version: COOKIE_TEXT_VERSION }) }).catch(() => {});
  } catch { /* logging must never block the choice */ }
};
export const setConsent = (value, key = CONSENT_KEY) => {
  ls.set(key, value);
  ls.set(`${key}_at`, String(Date.now()));
  window.dispatchEvent(new CustomEvent(CHANGED, { detail: { key, value } }));
  logConsent(value, key);
};
export const openCookieSettings = () => window.dispatchEvent(new Event(REOPEN));
export const onCookieSettings = (fn) => { window.addEventListener(REOPEN, fn); return () => window.removeEventListener(REOPEN, fn); };

export function useConsent(key = CONSENT_KEY) {
  const [value, setValue] = useState(() => getConsent(key));
  useEffect(() => {
    const h = (e) => { if (!e.detail || e.detail.key === key) setValue(e.detail ? e.detail.value : getConsent(key)); };
    const s = (e) => { if (e.key === key) setValue(e.newValue); };
    window.addEventListener(CHANGED, h);
    window.addEventListener("storage", s);
    return () => { window.removeEventListener(CHANGED, h); window.removeEventListener("storage", s); };
  }, [key]);
  return value;
}
